import { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { ScrollContainer } from '@/components/atom/QuestionsListItems/QuestionsListItems.styles';
import { gaPush, QUESTION_PANEL_ACTIONS, QUESTION_PANEL_CATEGORY } from '@/configuration/setup/googleAnalytics';
import useRovingTabIndex from '@/hooks/useRovingTabIndex';
import { EFaqsCategoryIcons } from '@/store/faq/config';
import { TFAQsItem } from '@/store/faq/types';
import { EOrientation } from '@/utils/constants';

import QuestionListItem from './QuestionListItem';
import { TQuestionsListItemsProps } from './types';

const QUESTION_LIST_LABEL = 'chatPage.sidebar.faq.label';
const ONBOARDING_QUESTION_ID = 'onboardingQuestion';

const QuestionsListItems = ({ isOnboarding, faqsList, searchValue, handleSelection }: TQuestionsListItemsProps) => {
  const intl = useIntl();

  const onboardingQuestion: TFAQsItem = useMemo(
    () => ({
      id: ONBOARDING_QUESTION_ID,
      name: intl.formatMessage({
        id: 'chatPage.servicesSection.exemplary_questions_onboarding_one',
      }),
      icon: EFaqsCategoryIcons.PERFORM,
      type: EFaqsCategoryIcons.PERFORM,
      tooltipText: 'chatPage.chatInput.tooltip.stats',
    }),
    [intl],
  );

  const faqsToRender = useMemo<TFAQsItem[]>(
    () => (isOnboarding ? [onboardingQuestion] : faqsList),
    [isOnboarding, onboardingQuestion, faqsList],
  );

  // INFO: Highlighter compares its props shallowly, so a fresh array on every keystroke would
  // re-run the mark computation for every item in the list.
  const searchWords = useMemo(() => [searchValue], [searchValue]);

  /** Reports the selection to analytics and hands it to the caller. The single activation path. */
  const trackAndSelect = useCallback(
    (faq: TFAQsItem) => {
      gaPush({
        category: QUESTION_PANEL_CATEGORY,
        action: QUESTION_PANEL_ACTIONS.SELECT_QUESTION_ITEM,
        label: faq.type,
      });
      handleSelection(faq);
    },
    [handleSelection],
  );

  const handleActivate = useCallback(
    (index: number) => {
      const faq = faqsToRender[index];

      if (!faq) return;

      trackAndSelect(faq);
    },
    [faqsToRender, trackAndSelect],
  );

  const { handleKeyDown, getTabIndex, setItemRef } = useRovingTabIndex({
    itemCount: faqsToRender.length,
    orientation: EOrientation.VERTICAL,
    onActivate: handleActivate,
  });

  const listLabel = intl.formatMessage({
    id: QUESTION_LIST_LABEL,
    defaultMessage: 'Example questions',
  });

  if (faqsToRender.length === 0) {
    return (
      <ScrollContainer
        role="group"
        aria-label={listLabel}
        data-testid="example-questions-list"
        className="list-group border rounded margin-bottom-20"
      >
        <div className="list-group-item list-group-item-link">
          <FormattedMessage id="chatPage.servicesSection.exemplary_questions.noExampleQuestions" />
        </div>
      </ScrollContainer>
    );
  }

  return (
    <ScrollContainer
      // INFO: The list keeps a single tab stop and moves between items with the arrow keys, so it
      // has to advertise itself as a composite widget. Without the role a keyboard user tabs past
      // the whole list after the first item, with nothing announcing that arrows reveal the rest.
      role="toolbar"
      aria-orientation={EOrientation.VERTICAL}
      aria-label={listLabel}
      data-testid="example-questions-list"
      className="list-group border rounded margin-bottom-20"
      onKeyDown={handleKeyDown}
    >
      {faqsToRender.map((faq, index) => (
        <QuestionListItem
          key={`filter-${faq.id}`}
          faq={faq}
          isOnboardingQuestion={faq.id === ONBOARDING_QUESTION_ID}
          searchWords={searchWords}
          tabIndex={getTabIndex(index)}
          itemRef={setItemRef(index)}
          onSelect={trackAndSelect}
        />
      ))}
    </ScrollContainer>
  );
};

export default QuestionsListItems;
